/**
 * Policy Intelligence Dashboard Page (F051)
 *
 * Route: /policy-intelligence | Nav: Enforcement > Policy Intelligence
 *
 * Shows policy effectiveness scores, top recommendations, worst-performing
 * policies, and governance drift. Wired entirely to live API data — an empty
 * or unavailable response renders an explicit empty/not-measured state, never
 * a fabricated fallback value.
 */
import React from 'react';
import {
  Title2,
  Body1,
  Body2,
  Caption1,
  Card,
  CardHeader,
  Badge,
  Button,
  Spinner,
  tokens,
  makeStyles,
} from '@fluentui/react-components';
import {
  CheckmarkRegular,
  DismissRegular,
} from '@fluentui/react-icons';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';
import {
  usePolicyIntelligenceSummary,
  usePolicyEffectiveness,
  usePolicyRecommendations,
  useAcceptRecommendation,
  useDismissRecommendation,
} from '../hooks/usePolicyIntelligence';

// ---------------------------------------------------------------------------
// Custom styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  recRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  recHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalM,
  },
  recActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    flexShrink: 0,
  },
  policyRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 0.5fr 1fr 0.7fr',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 0.5fr 1fr 0.7fr',
    gap: tokens.spacingHorizontalM,
    paddingBottom: tokens.spacingVerticalXS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getImpactColor = (impact: string): 'danger' | 'warning' | 'success' => {
  switch (impact) {
    case 'High': return 'danger';
    case 'Medium': return 'warning';
    case 'Low': return 'success';
    default: return 'warning';
  }
};

const getScoreColor = (score: number): string => {
  if (score >= 80) return tokens.colorPaletteGreenForeground1;
  if (score >= 60) return tokens.colorNeutralForeground1;
  if (score >= 40) return tokens.colorPaletteDarkOrangeForeground1;
  return tokens.colorPaletteRedForeground1;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const PolicyIntelligenceDashboardPage: React.FC = () => {
  const ps = usePageStyles();
  const styles = useStyles();

  const { data: summaryData, isLoading: summaryLoading, error: summaryError } = usePolicyIntelligenceSummary();
  const { data: effectivenessData } = usePolicyEffectiveness();
  const { data: recsData } = usePolicyRecommendations();
  const acceptMutation = useAcceptRecommendation();
  const dismissMutation = useDismissRecommendation();

  // Empty API responses render an honest empty state below — never a fabricated fallback value.
  const worstPolicies = (effectivenessData?.records ?? [])
    .slice()
    .sort((a, b) => a.score - b.score)
    .slice(0, 10);
  const topRecs = (recsData?.recommendations ?? [])
    .filter((r) => r.status === 'Pending')
    .slice(0, 5);

  // Loading state
  if (summaryLoading) {
    return (
      <div className={ps.page}>
        <PageHeader
          title="Policy Intelligence"
          subtitle="Effectiveness scores, recommendations, and governance drift detection"
        />
        <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXXL }}>
          <Spinner size="large" label="Loading intelligence data..." />
        </div>
      </div>
    );
  }

  // Error state
  if (summaryError && !summaryData) {
    return (
      <div className={ps.page}>
        <PageHeader
          title="Policy Intelligence"
          subtitle="Effectiveness scores, recommendations, and governance drift detection"
        />
        <Body1 style={{ color: tokens.colorPaletteRedForeground1, textAlign: 'center', padding: tokens.spacingVerticalXXL }}>
          Failed to load intelligence data. Please try again later.
        </Body1>
      </div>
    );
  }

  // Empty state — no summary payload, or a summary with zero recorded evaluations. Never
  // substitute demo numbers for a genuinely empty tenant.
  if (!summaryData || summaryData.totalEvaluations === 0) {
    return (
      <div className={ps.page}>
        <PageHeader
          title="Policy Intelligence"
          subtitle="Effectiveness scores, recommendations, and governance drift detection"
        />
        <div className={ps.emptyState}>
          <Title2>No Intelligence Data</Title2>
          <Body1 style={{ color: tokens.colorNeutralForeground3, marginTop: tokens.spacingVerticalS }}>
            System Status: No policy execution events recorded. Connect repositories and run policy evaluations to begin building intelligence.
          </Body1>
        </div>
      </div>
    );
  }

  const summary = summaryData;

  return (
    <div className={ps.page}>
      <PageHeader
        title="Policy Intelligence"
        subtitle="Effectiveness scores, recommendations, and governance drift detection"
      />

      {/* ── Stat Cards ──────────────────────────────────────────────────── */}
      <div className={ps.metricGrid}>
        <Card>
          <div className={ps.cardBody}>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Policy Effectiveness Score</Body2>
              <Title2 className={ps.metricValue} style={{ color: getScoreColor(summary.effectivenessScore) }}>
                {summary.effectivenessScore}%
              </Title2>
            </div>
          </div>
        </Card>
        <Card>
          <div className={ps.cardBody}>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Coverage Gap Score</Body2>
              <Title2 className={ps.metricValue}>{summary.coverageGapScore}%</Title2>
            </div>
          </div>
        </Card>
        <Card>
          <div className={ps.cardBody}>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Drift Score</Body2>
              <Title2 className={ps.metricValue}>{summary.driftScore}%</Title2>
            </div>
          </div>
        </Card>
        <Card>
          <div className={ps.cardBody}>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Recommendation Acceptance Rate</Body2>
              {/*
                Null means nothing has been accepted or dismissed yet, so there is no rate to
                report. Render the not-measured dash rather than coercing to 0 — a 0% here would
                read as "measured, and nobody accepts our recommendations", a different and worse
                claim. Same treatment as Benchmark Score above.
              */}
              <Title2
                className={ps.metricValue}
                style={
                  summary.recommendationAcceptanceRate == null
                    ? { color: tokens.colorNeutralForeground3 }
                    : undefined
                }
              >
                {summary.recommendationAcceptanceRate != null
                  ? `${summary.recommendationAcceptanceRate}%`
                  : '—'}
              </Title2>
              {summary.recommendationAcceptanceRate == null && (
                <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                  No recommendations decided yet
                </Caption1>
              )}
            </div>
          </div>
        </Card>
        <Card>
          <div className={ps.cardBody}>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Benchmark Score</Body2>
              <Title2 className={ps.metricValue} style={{ color: tokens.colorNeutralForeground3 }}>
                {summary.benchmarkScore != null ? `${summary.benchmarkScore}%` : '\u2014'}
              </Title2>
              {summary.benchmarkScore == null && (
                <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Not yet available</Caption1>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* ── Two-column: Recommendations + Worst Policies ────────────── */}
      <div className={ps.twoColumn}>
        {/* Top Recommendations */}
        <Card>
          <CardHeader
            header={<Title2>Top Recommendations</Title2>}
            description="Highest-impact actions to improve your governance posture"
          />
          <div className={ps.cardBody}>
            {topRecs.length === 0 ? (
              <Body1 style={{ color: tokens.colorNeutralForeground3, textAlign: 'center', padding: tokens.spacingVerticalL }}>
                No pending recommendations. Your governance posture is strong.
              </Body1>
            ) : (
              <div className={ps.rowList}>
                {topRecs.map((rec) => (
                  <div key={rec.id} className={styles.recRow}>
                    <div className={styles.recHeader}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS }}>
                        <Body1 style={{ fontWeight: 600 }}>{rec.title}</Body1>
                        <Body2 style={{ color: tokens.colorNeutralForeground3, fontStyle: 'italic' }}>
                          {rec.whyItMatters}
                        </Body2>
                      </div>
                      <Badge appearance="filled" color={getImpactColor(rec.estimatedImpact)} size="small">
                        {rec.estimatedImpact}
                      </Badge>
                    </div>
                    <div className={styles.recActions}>
                      <Button
                        appearance="primary"
                        size="small"
                        icon={<CheckmarkRegular />}
                        disabled={acceptMutation.isPending}
                        onClick={() => acceptMutation.mutate(rec.id)}
                      >
                        Accept
                      </Button>
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<DismissRegular />}
                        disabled={dismissMutation.isPending}
                        onClick={() => dismissMutation.mutate(rec.id)}
                      >
                        Dismiss
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* Worst Performing Policies */}
        <Card>
          <CardHeader
            header={<Title2>Worst Performing Policies</Title2>}
            description="Bottom 10 policies by effectiveness score"
          />
          <div className={ps.cardBody}>
            {worstPolicies.length === 0 ? (
              <Body1 style={{ color: tokens.colorNeutralForeground3, textAlign: 'center', padding: tokens.spacingVerticalL }}>
                No effectiveness data recorded yet.
              </Body1>
            ) : (
              <>
            <div className={styles.tableHeader}>
              <Caption1 style={{ fontWeight: 600, color: tokens.colorNeutralForeground3 }}>Policy Name</Caption1>
              <Caption1 style={{ fontWeight: 600, color: tokens.colorNeutralForeground3 }}>Family</Caption1>
              <Caption1 style={{ fontWeight: 600, color: tokens.colorNeutralForeground3 }}>Score</Caption1>
              <Caption1 style={{ fontWeight: 600, color: tokens.colorNeutralForeground3 }}>Classification</Caption1>
              <Caption1 style={{ fontWeight: 600, color: tokens.colorNeutralForeground3 }}>Executions</Caption1>
            </div>
            <div className={ps.rowList}>
              {worstPolicies.map((p) => (
                <div key={p.policyId} className={styles.policyRow}>
                  <Body1 style={{ fontWeight: 500 }}>{p.policyName}</Body1>
                  <Caption1>{p.family}</Caption1>
                  <Body2 className={ps.metricValue} style={{ fontWeight: 600, color: getScoreColor(p.score) }}>
                    {p.score}%
                  </Body2>
                  <Badge
                    appearance="outline"
                    color={p.classification === 'Critical' ? 'danger' : p.classification === 'High' ? 'important' : 'warning'}
                    size="small"
                  >
                    {p.classification}
                  </Badge>
                  <Body2 style={{ color: tokens.colorNeutralForeground3 }}>{p.executions.toLocaleString()}</Body2>
                </div>
              ))}
            </div>
              </>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};
